import { useEffect, useState } from "react";
import { useSettings, type ImageProvider } from "../store/settings";
import { useProfile } from "../store/profile";
import { useNotion } from "../store/notion";
import { pingHealth, pushAll, pullAll, listRemote, getApiBase, getApiToken, getUserId } from "../lib/cloudSync";
import { notionPing } from "../lib/notionRag";
import {
  localStorageKeySizeBytes, localStorageTotalSizeBytes, humanBytes,
  resizeForLogo, resizeForSpokesperson, resizeForAvatar, resizeForReference
} from "../lib/imageResize";
import { useTasks } from "../store/tasks";

const CLAUDE_MODELS = [
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6 (recomendado)" },
  { id: "claude-opus-4-7",   label: "Opus 4.7 (mais inteligente)" },
  { id: "claude-haiku-4-5",  label: "Haiku 4.5 (rápido/barato)" }
];

const GEMINI_IMG_MODELS = [
  { id: "gemini-3-pro-image-preview",     label: "Nano Banana Pro (melhor qualidade)" },
  { id: "gemini-3.1-flash-image-preview", label: "Nano Banana Normal (rápido + bom)" },
  { id: "gemini-2.5-flash-image",         label: "Nano Banana Light (mais rápido)" }
];

const OPENAI_IMG_MODELS = [
  { id: "gpt-image-2", label: "gpt-image-2 (recomendado, novo)" },
  { id: "gpt-image-1", label: "gpt-image-1" },
  { id: "dall-e-3",    label: "DALL·E 3 (legado)" }
];

const HIGGS_MODELS = [
  { id: "higgsfield-soul", label: "Soul (premium)" },
  { id: "higgsfield-dop",  label: "DoP (cinematic)" },
  { id: "higgsfield-lite", label: "Lite (rápido)" }
];

export function Settings({ onClose }: { onClose: () => void }) {
  const s = useSettings();
  const [show, setShow] = useState<Record<string, boolean>>({});

  const masked = (k: string) =>
    k ? k.slice(0, 8) + "…" + k.slice(-4) : "(não definida)";

  const KeyRow = ({ id, label, value, set, placeholder }: {
    id: string; label: string; value: string; set: (v: string) => void; placeholder: string;
  }) => {
    const [draft, setDraft] = useState(value);
    return (
      <div>
        <div className="font-display font-bold uppercase text-base">{label}</div>
        <div className="tag text-graydk mb-1">atual: <span className="font-mono normal-case">{masked(value)}</span></div>
        <div className="flex gap-2">
          <input
            type={show[id] ? "text" : "password"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            className="flex-1 border border-ink bg-bone px-3 py-2 font-mono text-[12px]"
          />
          <button onClick={() => setShow(s => ({ ...s, [id]: !s[id] }))} className="btn btn-fire">
            {show[id] ? "ocultar" : "ver"}
          </button>
          <button onClick={() => { set(draft.trim()); alert(label + " salva."); }} className="btn btn-hire">salvar</button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-3xl max-h-[92vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-5 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="font-mono text-xs text-gold">CFG-01</div>
            <h2 className="font-display font-black uppercase text-3xl leading-none">CONFIGURAÇÕES</h2>
            <div className="font-serif italic text-bone/70 text-sm mt-1">
              chaves de API ficam no localStorage do navegador
            </div>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone">fechar ×</button>
        </header>

        <div className="p-5 space-y-6">
          <ProfileSection />
          <CloudSyncSection />
          <NotionSection />
          <StorageSection />

          {/* ===== ANTHROPIC (text) ===== */}
          <section className="border-2 border-ink p-4">
            <div className="font-display font-black text-xl uppercase mb-3">▸ Anthropic · Claude (texto)</div>
            <KeyRow id="ant" label="API Key" value={s.anthropicKey} set={s.setKey} placeholder="sk-ant-…" />
            <div className="mt-3">
              <div className="tag text-graydk mb-1">modelo padrão</div>
              <div className="flex flex-col gap-1">
                {CLAUDE_MODELS.map(m => (
                  <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={s.model === m.id} onChange={() => s.setModel(m.id)} />
                    <span className="font-mono text-[12px]">{m.id}</span>
                    <span className="font-crimson italic text-paper text-sm">— {m.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* ===== GEMINI (img) ===== */}
          <section className="border-2 border-ink p-4">
            <div className="font-display font-black text-xl uppercase mb-3">▸ Google · Gemini (imagem)</div>
            <KeyRow id="gem" label="API Key" value={s.geminiKey} set={s.setGeminiKey} placeholder="AIza…" />
            <div className="mt-3">
              <div className="tag text-graydk mb-1">modelo de imagem</div>
              <div className="flex flex-col gap-1">
                {GEMINI_IMG_MODELS.map(m => (
                  <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={s.geminiImageModel === m.id} onChange={() => s.setGeminiImageModel(m.id)} />
                    <span className="font-mono text-[12px]">{m.id}</span>
                    <span className="font-crimson italic text-paper text-sm">— {m.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* ===== OPENAI (img) ===== */}
          <section className="border-2 border-ink p-4">
            <div className="font-display font-black text-xl uppercase mb-3">▸ OpenAI · gpt-image (imagem)</div>
            <KeyRow id="oai" label="API Key" value={s.openaiKey} set={s.setOpenaiKey} placeholder="sk-…" />
            <div className="mt-3">
              <div className="tag text-graydk mb-1">modelo de imagem</div>
              <div className="flex flex-col gap-1">
                {OPENAI_IMG_MODELS.map(m => (
                  <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={s.openaiImageModel === m.id} onChange={() => s.setOpenaiImageModel(m.id)} />
                    <span className="font-mono text-[12px]">{m.id}</span>
                    <span className="font-crimson italic text-paper text-sm">— {m.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* ===== HIGGSFIELD (video) ===== */}
          <section className="border-2 border-ink p-4">
            <div className="font-display font-black text-xl uppercase mb-3">▸ Higgsfield (vídeo)</div>
            <KeyRow id="hf" label="API Key" value={s.higgsfieldKey} set={s.setHiggsfieldKey} placeholder="hf_…" />
            <div className="mt-3">
              <div className="tag text-graydk mb-1">modelo de vídeo</div>
              <div className="flex flex-col gap-1">
                {HIGGS_MODELS.map(m => (
                  <label key={m.id} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={s.higgsfieldModel === m.id} onChange={() => s.setHiggsfieldModel(m.id)} />
                    <span className="font-mono text-[12px]">{m.id}</span>
                    <span className="font-crimson italic text-paper text-sm">— {m.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* ===== PROVIDER PADRÃO ===== */}
{/* end-of-marker */}
          <section className="border-2 border-ink p-4">
            <div className="font-display font-black text-xl uppercase mb-3">▸ Provedor padrão de imagem</div>
            <div className="flex gap-2">
              {(["gemini","openai"] as ImageProvider[]).map(p => (
                <button
                  key={p}
                  onClick={() => s.setImageProvider(p)}
                  className={`btn ${s.imageProvider === p ? "bg-ink text-bone border-ink" : "btn-fire"}`}>
                  {p}
                </button>
              ))}
            </div>
            <p className="font-crimson italic text-paper text-sm mt-2">
              Usado quando você clica em "gerar imagem" sem especificar provedor.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function ProfileSection() {
  const p = useProfile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: p.name, email: p.email, phone: p.phone });
  const [newCo, setNewCo] = useState({ name: "", role: "" });

  const save = () => {
    p.setProfile({ name: draft.name.trim(), email: draft.email.trim(), phone: draft.phone.trim() });
    setEditing(false);
  };

  return (
    <section className="border-2 border-ink p-4 bg-gold/10">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-graydk">PERFIL</div>
          <div className="font-display font-black uppercase text-xl leading-none">▸ Seu perfil</div>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="btn btn-fire">editar</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="btn btn-fire">cancelar</button>
            <button onClick={save} className="btn btn-hire">salvar</button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[13px] font-mono">
          <Info label="nome" value={p.name} />
          <Info label="email" value={p.email} />
          <Info label="telefone" value={p.phone || "—"} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="nome" value={draft.name}  onChange={(v) => setDraft(d => ({ ...d, name: v }))} />
          <Field label="email" value={draft.email} onChange={(v) => setDraft(d => ({ ...d, email: v }))} type="email" />
          <Field label="telefone" value={draft.phone} onChange={(v) => setDraft(d => ({ ...d, phone: v }))} />
        </div>
      )}

      {/* EMPRESAS */}
      <div className="mt-5 border-t border-ink/30 pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-bold uppercase text-base">Empresas</div>
          <div className="tag text-graydk">{p.companies.length} vinculada{p.companies.length === 1 ? "" : "s"}</div>
        </div>

        <div className="space-y-2">
          {p.companies.map(c => (
            <div key={c.id}
              className={`border-2 p-2 flex items-center gap-2
                ${c.isPrimary ? "border-red bg-bone" : "border-ink/30 bg-bone"}`}>
              <span className={`inline-block w-3 h-3 ${c.isPrimary ? "bg-red" : "bg-graydk"}`} />
              <div className="flex-1 min-w-0">
                <input
                  value={c.name}
                  onChange={(e) => p.updateCompany(c.id, { name: e.target.value })}
                  className="w-full bg-transparent font-display font-bold uppercase text-base outline-none"
                />
                <input
                  value={c.role ?? ""}
                  placeholder="cargo / função"
                  onChange={(e) => p.updateCompany(c.id, { role: e.target.value })}
                  className="w-full bg-transparent font-crimson italic text-[12px] text-paper outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                {!c.isPrimary && (
                  <button onClick={() => p.setPrimary(c.id)} className="tag text-red underline">
                    tornar principal
                  </button>
                )}
                {c.isPrimary && <span className="tag bg-red text-bone px-1.5">principal</span>}
                <button onClick={() => {
                  if (p.companies.length <= 1) { alert("Mantenha ao menos 1 empresa."); return; }
                  if (confirm(`Remover ${c.name}?`)) p.removeCompany(c.id);
                }} className="tag text-red underline">remover</button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex gap-2 items-end">
          <Field label="nova empresa" value={newCo.name}
                 onChange={(v) => setNewCo(c => ({ ...c, name: v }))} />
          <Field label="cargo" value={newCo.role}
                 onChange={(v) => setNewCo(c => ({ ...c, role: v }))} />
          <button
            onClick={() => {
              if (!newCo.name.trim()) return;
              p.addCompany({ name: newCo.name.trim(), role: newCo.role.trim() || undefined });
              setNewCo({ name: "", role: "" });
            }}
            className="btn btn-hire">+ adicionar</button>
        </div>
      </div>
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tag text-graydk">{label}</div>
      <div className="font-mono text-[13px] truncate">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }:
  { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="flex-1 min-w-[140px]">
      <div className="tag text-graydk mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[13px]"
      />
    </div>
  );
}

function CloudSyncSection() {
  const [base, setBase]   = useState(getApiBase());
  const [token, setToken] = useState(getApiToken());
  const [userId, setUserId] = useState(getUserId());
  const [online, setOnline] = useState<boolean | null>(null);
  const [remote, setRemote] = useState<{ store_key: string; version: number; updated_at: string }[]>([]);
  const [log, setLog]     = useState<string[]>([]);
  const [busy, setBusy]   = useState(false);

  useEffect(() => { pingHealth().then(setOnline); }, [base]);

  const saveCreds = () => {
    localStorage.setItem("vpc-api-base", base.trim());
    localStorage.setItem("vpc-api-token", token.trim());
    localStorage.setItem("vpc-user-id", userId.trim() || "marcio");
    pingHealth().then(setOnline);
  };

  const refresh = async () => {
    try { setRemote(await listRemote()); }
    catch (e) { setLog(l => [...l, "✗ list: " + (e as Error).message]); }
  };
  useEffect(() => { if (online) refresh(); }, [online]);

  const onPush = async () => {
    setBusy(true);
    setLog(l => [...l, "▸ enviando…"]);
    try {
      const v = await pushAll();
      setLog(l => [...l, "✓ push: " + Object.keys(v).length + " stores · versions " + JSON.stringify(v)]);
      await refresh();
    } catch (e) { setLog(l => [...l, "✗ " + (e as Error).message]); }
    finally { setBusy(false); }
  };

  const onPull = async () => {
    if (!confirm("Baixar do servidor SOBRESCREVE seu localStorage atual. Continuar?")) return;
    setBusy(true);
    setLog(l => [...l, "▸ baixando…"]);
    try {
      const { pulled, missing } = await pullAll();
      setLog(l => [...l, `✓ pull: ${pulled.length} ok, ${missing.length} faltando`]);
      if (pulled.length > 0) {
        setLog(l => [...l, "↻ recarregando em 2s pra aplicar…"]);
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (e) { setLog(l => [...l, "✗ " + (e as Error).message]); }
    finally { setBusy(false); }
  };

  return (
    <section className="border-2 border-ink p-4 bg-red/10">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-graydk">CLOUD-SYNC</div>
          <div className="font-display font-black uppercase text-xl leading-none">▸ Cloud Sync (DB)</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-block w-3 h-3 ${online ? "bg-ink" : online === false ? "bg-red" : "bg-graymd"}`} />
          <span className="tag text-graydk">{online ? "online" : online === false ? "offline" : "verificando…"}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-2 mb-3">
        <Field label="API base"    value={base}   onChange={setBase}   />
        <Field label="auth token"  value={token}  onChange={setToken}  type="password" />
        <Field label="user id"     value={userId} onChange={setUserId} />
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={saveCreds} className="btn btn-hire">salvar conexão</button>
        <button onClick={refresh} disabled={!online} className="btn btn-fire disabled:opacity-50">↻ listar</button>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <button onClick={onPush} disabled={!online || busy}
                className="btn bg-ink text-bone border-ink disabled:opacity-50 py-2">
          ↑ ENVIAR meu localStorage → servidor
        </button>
        <button onClick={onPull} disabled={!online || busy}
                className="btn bg-red text-bone border-red disabled:opacity-50 py-2">
          ↓ BAIXAR do servidor → localStorage
        </button>
      </div>

      {remote.length > 0 && (
        <div className="mb-3">
          <div className="tag text-graydk mb-1">stores no servidor ({remote.length})</div>
          <div className="grid grid-cols-2 gap-1 font-mono text-[11px]">
            {remote.map(r => (
              <div key={r.store_key} className="bg-bone border border-ink/30 px-2 py-1 flex justify-between">
                <span className="truncate">{r.store_key}</span>
                <span className="text-graydk">v{r.version}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div>
          <div className="tag text-graydk mb-1">log</div>
          <pre className="bg-ink/95 text-bone font-mono text-[11px] p-2 max-h-40 overflow-y-auto whitespace-pre-wrap">
{log.join("\n")}
          </pre>
        </div>
      )}

      <p className="font-crimson italic text-paper text-[11px] mt-2">
        Sincroniza RH, demandas, clientes, brand kits, sociais, boletins, perfil.
        Chaves de API NÃO são enviadas — ficam só no seu navegador.
      </p>
    </section>
  );
}

function StorageSection() {
  const [total, setTotal] = useState<number>(localStorageTotalSizeBytes());
  const [perKey, setPerKey] = useState<{ key: string; size: number }[]>([]);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const refresh = () => {
    setTotal(localStorageTotalSizeBytes());
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
    setPerKey(keys.map(k => ({ key: k, size: localStorageKeySizeBytes(k) }))
      .sort((a, b) => b.size - a.size));
  };

  useState(() => { refresh(); return null; });

  const compactBrandKit = async () => {
    setBusy(true);
    setLog(["▸ compactando imagens dos brand kits..."]);
    try {
      const raw = localStorage.getItem("agencia-vpc-clientbrand-v1");
      if (!raw) { setLog(l => [...l, "(vazio)"]); return; }
      const obj = JSON.parse(raw);
      const overrides = obj.state?.overrides ?? {};
      let savedBytes = 0;
      for (const [cid, ov] of Object.entries(overrides) as any) {
        if (ov.logoUrl?.startsWith("data:")) {
          const before = ov.logoUrl.length;
          try {
            ov.logoUrl = await resizeForLogo(ov.logoUrl);
            savedBytes += before - ov.logoUrl.length;
            setLog(l => [...l, `  logo ${cid}: -${humanBytes(before - ov.logoUrl.length)}`]);
          } catch {}
        }
        if (ov.spokespersonUrl?.startsWith("data:")) {
          const before = ov.spokespersonUrl.length;
          try {
            ov.spokespersonUrl = await resizeForSpokesperson(ov.spokespersonUrl);
            savedBytes += before - ov.spokespersonUrl.length;
            setLog(l => [...l, `  spokes ${cid}: -${humanBytes(before - ov.spokespersonUrl.length)}`]);
          } catch {}
        }
      }
      obj.state.overrides = overrides;
      localStorage.setItem("agencia-vpc-clientbrand-v1", JSON.stringify(obj));
      setLog(l => [...l, `✓ economizado: ${humanBytes(savedBytes)}`]);
      refresh();
    } catch (e) {
      setLog(l => [...l, "✗ " + (e as Error).message]);
    } finally { setBusy(false); }
  };

  const compactAvatars = async () => {
    setBusy(true);
    setLog(["▸ compactando avatares..."]);
    try {
      const raw = localStorage.getItem("agencia-vpc-rh-v2");
      if (!raw) return;
      const obj = JSON.parse(raw);
      const emps = obj.state?.employees ?? {};
      let saved = 0;
      for (const [id, e] of Object.entries(emps) as any) {
        if (e.avatarUrl?.startsWith("data:")) {
          const before = e.avatarUrl.length;
          try {
            e.avatarUrl = await resizeForAvatar(e.avatarUrl);
            saved += before - e.avatarUrl.length;
          } catch {}
        }
      }
      localStorage.setItem("agencia-vpc-rh-v2", JSON.stringify(obj));
      setLog(l => [...l, `✓ avatares: -${humanBytes(saved)}`]);
      refresh();
    } finally { setBusy(false); }
  };

  const clearImages = (storeKey: string, fields: string[]) => {
    if (!confirm(`Apagar ${fields.join(",")} de ${storeKey}? (re-gere depois)`)) return;
    const raw = localStorage.getItem(storeKey);
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (storeKey === "agencia-vpc-clientbrand-v1") {
      for (const ov of Object.values(obj.state?.overrides ?? {}) as any) {
        fields.forEach(f => delete ov[f]);
      }
    }
    if (storeKey === "agencia-vpc-rh-v2") {
      for (const e of Object.values(obj.state?.employees ?? {}) as any) {
        fields.forEach(f => delete e[f]);
      }
    }
    localStorage.setItem(storeKey, JSON.stringify(obj));
    setLog(l => [...l, `✗ removidos: ${fields.join(",")} de ${storeKey}`]);
    refresh();
  };

  return (
    <section className="border-2 border-ink p-4 bg-paper/5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-graydk">STORAGE</div>
          <div className="font-display font-black uppercase text-xl leading-none">▸ Armazenamento local</div>
        </div>
        <div className="text-right">
          <div className="tag text-graydk">total localStorage</div>
          <div className="font-display font-black text-2xl">{humanBytes(total)}</div>
          <div className="tag text-graydk">limite ~5MB</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-2 mb-3">
        <button onClick={compactBrandKit} disabled={busy} className="btn bg-ink text-bone border-ink disabled:opacity-50">
          🗜️ compactar logos + spokespersons
        </button>
        <button onClick={compactAvatars} disabled={busy} className="btn bg-ink text-bone border-ink disabled:opacity-50">
          🗜️ compactar avatares
        </button>
        <button onClick={() => {
          if (!confirm("Compactar tasks?\n\nMantém: 60 tasks mais recentes, 5 outputs/task, trunca conteúdo > 8KB. Reversível só com backup JSON.")) return;
          const r = useTasks.getState().compact();
          setLog(l => [...l, `🗜️ tasks compactadas: -${humanBytes(r.freed)} · ${r.tasksAffected} tasks afetadas`]);
          refresh();
        }} className="btn bg-ink text-bone border-ink">
          🗜️ compactar TASKS (outputs)
        </button>
        <button onClick={() => {
          if (!confirm("⚠ APAGAR TODAS as tasks?\n\nDesfaz com 'Importar JSON' se tiver backup.")) return;
          useTasks.getState().clearAll();
          setLog(l => [...l, "✗ todas as tasks apagadas"]);
          refresh();
        }} className="btn bg-red text-bone border-red">
          ✗ apagar TODAS tasks
        </button>
        <button onClick={() => clearImages("agencia-vpc-clientbrand-v1", ["logoUrl","spokespersonUrl"])}
                className="btn bg-red text-bone border-red">
          ✗ apagar imagens brand kit
        </button>
        <button onClick={() => clearImages("agencia-vpc-rh-v2", ["avatarUrl"])}
                className="btn bg-red text-bone border-red">
          ✗ apagar avatares
        </button>
      </div>

      <details>
        <summary className="cursor-pointer tag">ver breakdown ({perKey.length} keys)</summary>
        <div className="mt-2 space-y-1 font-mono text-[11px] max-h-40 overflow-y-auto">
          {perKey.map(p => (
            <div key={p.key} className="flex justify-between border-b border-ink/10 py-0.5">
              <span className="truncate">{p.key}</span>
              <span className={p.size > 500_000 ? "text-red font-bold" : "text-graydk"}>{humanBytes(p.size)}</span>
            </div>
          ))}
        </div>
      </details>

      {log.length > 0 && (
        <pre className="mt-2 bg-ink/95 text-bone font-mono text-[11px] p-2 max-h-40 overflow-y-auto whitespace-pre-wrap">
{log.join("\n")}
        </pre>
      )}

      <p className="font-crimson italic text-paper text-[11px] mt-2">
        localStorage tem limite de ~5MB por origem. Imagens base64 enchem rápido. Use "Cloud Sync" pra liberar e
        manter dados no DB. Logos comprimidos pra ≤512px, avatares ≤384px, spokespersons ≤768px.
      </p>
    </section>
  );
}

function NotionSection() {
  const n = useNotion();
  const [draft, setDraft] = useState(n.token);
  const [show, setShow] = useState(false);
  const [pingState, setPingState] = useState<"idle" | "ok" | "fail">("idle");
  const [busy, setBusy] = useState(false);

  const test = async () => {
    if (!draft.trim()) return;
    setBusy(true); setPingState("idle");
    try {
      const ok = await notionPing(draft.trim());
      setPingState(ok ? "ok" : "fail");
      if (ok) n.setToken(draft.trim());
    } catch { setPingState("fail"); }
    finally { setBusy(false); }
  };

  return (
    <section className="border-2 border-ink p-4 bg-gold/10">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-graydk">RAG · NOTION</div>
          <div className="font-display font-black uppercase text-xl leading-none">▸ Base de conhecimento (Notion)</div>
          <div className="font-crimson italic text-paper text-sm mt-1">
            Quando ativo, o pipeline busca contexto nas suas páginas Notion antes de cada geração
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-block w-3 h-3 ${pingState === "ok" ? "bg-ink" : pingState === "fail" ? "bg-red" : "bg-graymd"}`} />
          <span className="tag text-graydk">
            {pingState === "ok" ? "conectado" : pingState === "fail" ? "falha" : "—"}
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="tag text-graydk mb-1">token Notion (ntn_…)</div>
          <div className="flex gap-1">
            <input
              type={show ? "text" : "password"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="ntn_..."
              className="flex-1 border border-ink bg-bone px-2 py-1 font-mono text-[12px]"
            />
            <button onClick={() => setShow(s => !s)} className="btn btn-fire">{show ? "ocultar" : "ver"}</button>
            <button onClick={test} disabled={busy || !draft.trim()} className="btn bg-ink text-bone border-ink disabled:opacity-50">
              {busy ? "testando…" : "testar"}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={n.enabled} onChange={(e) => n.setEnabled(e.target.checked)} />
            <span className="font-mono text-[13px]">RAG habilitado</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={n.injectInGeneration} onChange={(e) => n.setInject(e.target.checked)} />
            <span className="font-mono text-[13px]">Injetar contexto Notion no pipeline</span>
          </label>
          <p className="font-crimson italic text-paper text-[11px]">
            Quando ligado, cada demanda busca as 5 páginas Notion mais relevantes (via Search API) e adiciona ao briefing antes do time executar.
          </p>
        </div>
      </div>
    </section>
  );
}
